// TODO: Flattened console output is a workaround for Chromium nested group bug
// https://issues.chromium.org/issues/443117143
// Revert to nested console.group structure once resolved
import type {OperationInfo, ServerExtensions, ServiceCalls} from "@irati/types/apolloExtensions"
import {ApolloLink, Operation, FetchResult} from "@apollo/client"
import {print} from "graphql/language/printer"
import {buildApolloWaterfall} from "./apolloWaterfall"

const BATCH_DEBOUNCE_MS = 10
const WARNING = "⛔️"
const ERROR = "❌"
const SUCCESS = "✅"

export interface ApolloDebugEvent {
  name: string
  attributes: unknown
}

const debugEventListeners = new Set<(event: ApolloDebugEvent) => void>()

/**
 * Debug-only: listeners receive full response `data` and `variables`, which may contain PII.
 * Events only fire for responses carrying the `loggingV1` extension, which the graphql server
 * never sends in production (see ApolloServerPluginLoggingV1 in apps/graphql/server/createServer.ts).
 * Do not use this for production logging or analytics.
 */
export function onApolloEvent(listener: (event: ApolloDebugEvent) => void): () => void {
  debugEventListeners.add(listener)
  return () => debugEventListeners.delete(listener)
}

function notifyDebugEventListeners(event: ApolloDebugEvent) {
  debugEventListeners.forEach((listener) => {
    try {
      listener(event)
    } catch {
      // a debug-panel listener failing must never affect apollo logging
    }
  })
}

type Seen = {
  timeoutHandle?: ReturnType<typeof setTimeout>
  startedAt?: number
  endedAt?: number
  ops: Map<
    Operation,
    {
      info?: OperationInfo
      result: FetchResult
      clientTiming: {startedAt: number; endedAt: number; durationMs: number}
    }
  >
}

// simple logger for dev and staging to show apollo queries in console. Will
// show batches if available
export const apolloLogger = () => {
  const seenQueries = new Map<string, Seen>()

  return new ApolloLink((operation, forward) => {
    const start = performance.now()
    const clientStartedAt = Date.now()

    return forward(operation).map((data) => {
      if (hasLoggingV1(data.extensions)) {
        const durationMs = performance.now() - start
        const {services, correlationId, operations, startedAt, endedAt} = data.extensions.loggingV1
        const key = correlationId
        let seen = seenQueries.get(correlationId)

        if (!seen) {
          seen = {ops: new Map()}
          seenQueries.set(key, seen)
        }

        if (startedAt !== undefined) seen.startedAt = Math.min(seen.startedAt ?? startedAt, startedAt)
        if (endedAt !== undefined) seen.endedAt = Math.max(seen.endedAt ?? endedAt, endedAt)

        const handle = setTimeout(log(key, seenQueries, services), BATCH_DEBOUNCE_MS)

        // debounce our timer
        clearTimeout(seen.timeoutHandle)
        const operationInfo = operations[operation.operationName]
        seen.ops.set(operation, {
          info: operationInfo,
          result: data,
          clientTiming: {startedAt: clientStartedAt, endedAt: clientStartedAt + durationMs, durationMs}
        })
        // create a new debounce AFTER clearing the old one
        seen.timeoutHandle = handle
      }
      return data
    })
  })
}

function hasLoggingV1(extensions: unknown): extensions is ServerExtensions {
  return (
    extensions && //
    typeof extensions === "object" &&
    extensions !== null &&
    "loggingV1" in extensions &&
    (extensions.loggingV1 as any)
  )
}

function log(key: string, seenQueries: Map<string, Seen>, services: ServiceCalls) {
  return () => {
    const known = seenQueries.get(key)
    if (!known) {
      console.warn("Impossible! this should have been set when the handle was created")
    } else {
      const output = Array.from(known.ops.entries()).map(
        ([
          {operationName, variables, query},
          {
            result: {data, errors, extensions},
            info,
            clientTiming
          }
        ]) => {
          return {
            operation: operationName,
            variables,
            query: print(query),
            data,
            errors,
            duration: info?.durationMs,
            timingRecord: hasLoggingV1(extensions) ? extensions.loggingV1.timing : undefined,
            clientTiming
          } as const
        }
      )
      const errorCalls = output.filter((o) => o.errors?.length).map((o) => o.operation)
      const successCalls = output.filter((o) => !o.errors?.length).map((o) => o.operation)
      const joinChar = " | "
      const hasError = errorCalls.length > 0
      const hasSuccess = successCalls.length > 0
      const warnings = getWarnings(services)

      const labelInfo = !hasError
        ? successCalls.join(joinChar)
        : [
            hasSuccess && `${SUCCESS} ${successCalls.join(joinChar)}`, //
            `${ERROR} ${errorCalls.join(joinChar)}`
          ]
            .filter(Boolean)
            .join("\n")

      const reqTime = Math.floor(Math.max(...output.map(({clientTiming}) => clientTiming.durationMs)))
      console.groupCollapsed(`🚀 Graphql (${reqTime}ms)${warnings.hasWarnings ? ` ${WARNING}` : ""}:\n${labelInfo}`)

      if (warnings.hasWarnings) {
        console.warn("Possible DataLoader optimisation:\n" + warnings.warnings.join("\n"))
      }

      output.forEach(({query, operation, duration, errors, variables, data, clientTiming, timingRecord}) => {
        notifyDebugEventListeners({name: operation, attributes: {duration, errors, variables, data}})

        if (output.length > 1) {
          const hasErrors = errors?.length
          const timing = duration === undefined ? "" : ` [${Math.floor(duration)}ms]`
          console.log(`%c── ${operation}${timing} ${hasErrors ? ERROR : SUCCESS}`, "font-weight: 800")
        }

        if (errors?.length) {
          console.log("%cErrors", "color: coral; font-weight: 800", errors)
        }

        // console.table is for people; console.info is also captured by the local JSONL bridge.
        if (timingRecord) {
          console.info({...timingRecord, client: {operationName: operation, ...clientTiming}})
        }
        console.log("%cClient timing", "font-weight: 800", {
          startedAt: new Date(clientTiming.startedAt).toISOString(),
          endedAt: new Date(clientTiming.endedAt).toISOString(),
          durationMs: Math.round(clientTiming.durationMs * 10) / 10
        })
        console.log("%cGraphQL Response", "color: #0096ff; font-weight: 800", data)

        if (Object.keys(variables).length) {
          console.log("%cVariables", "font-weight: 800", variables)
        }

        const signature = query.slice(0, query.indexOf("{")).trim()
        console.log("%cQuery", "font-weight: 800", signature)
      })

      const waterfall = buildApolloWaterfall({
        logging: {
          correlationId: key,
          startedAt: known.startedAt,
          endedAt: known.endedAt,
          operations: Object.fromEntries(
            Array.from(known.ops).flatMap(([operation, {info}], index) =>
              info ? [[`${operation.operationName}${known.ops.size > 1 ? ` #${index + 1}` : ""}`, info]] : []
            )
          ),
          services
        }
      })
      if (waterfall.length) {
        console.log("%cRequest waterfall (server)", "font-weight: 800")
        console.log(
          "Offsets from the earliest server event; bars share one scale. Overlapping bars indicate concurrent requests. " +
            "GraphQL rows contain backend calls, so durations are not additive. Client timing above uses a separate clock."
        )
        console.table(waterfall, ["Request", "Duration (ms)", "Waterfall"])
      }

      if (Object.keys(services).length) {
        console.log(`%cBackend Services${warnings.hasWarnings ? ` ${WARNING}` : ""}`, "font-weight: 800")
        for (const [service, serviceData] of Object.entries(services)) {
          serviceData.forEach((d) => {
            if (d._tag === "ApiData") {
              console.log(`  ${service}/${d.endpoint} [${Math.floor(d.duration)}ms] [Api]`, d.data)
            } else if (d._tag === "GrpcData") {
              console.log(`  ${d.service}.${d.method} [${Math.floor(d.duration)}ms] [Grpc]`, d.data)
            } else if (d._tag === "AlgoliaData") {
              console.log(`  ${service}/Algolia [${Math.floor(d.duration)}ms] [Algolia]`, d.data)
            } else if (d._tag === "VivupData") {
              console.log(`  ${service} [${Math.floor(d.duration)}ms] [Vivup]`, d.data)
            }
          })
        }
      }

      console.groupEnd()

      // cleanup
      seenQueries.delete(key)
    }
  }
}

function getWarnings(services: ServiceCalls) {
  const map = new Map<string, Set<any>>()
  const warnings: string[] = []
  Object.entries(services).forEach(([name, calls]) => {
    calls.forEach((info) => {
      if (info._tag === "ApiData") {
        if (!map.has(info.url)) map.set(info.url, new Set())
        const set = map.get(info.url)!
        if (set.has(info.url)) {
          warnings.push(`endpoint called multiple times! ${info.endpoint}`)
        } else {
          set.add(info.url)
        }
      } else if (info._tag === "GrpcData") {
        const api = `${name}.${info.method}`
        if (!map.has(api)) map.set(api, new Set())
        const set = map.get(api)!
        if (set.has(info.variables)) {
          warnings.push(`${api} hit multiple times!`)
        } else {
          set.add(info.variables)
        }
      }
    })
  })

  return {hasWarnings: warnings.length > 0, warnings}
}
